import axios from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../utils/constants';

const API_URL = `${API_BASE_URL}/auth`;

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Add request interceptor to include auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access, refresh } = response.data.data;
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Refresh token failed, logout user
        localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Login user
 */
const login = async (mobile_number, password) => {
  const response = await axios.post(`${API_URL}/login/`, {
    mobile_number,
    password,
  });
  
  if (response.data.status === 'success') {
    const { access, refresh, user } = response.data.data;
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }
  
  return response.data;
};

/**
 * Logout user
 */
const logout = async () => {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  
  try {
    await axios.post(`${API_URL}/logout/`, {
      refresh_token: refreshToken,
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }
};

/**
 * Refresh access token
 */
const refreshToken = async () => {
  const refresh = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const response = await axios.post(`${API_URL}/token/refresh/`, { refresh });
  
  if (response.data.status === 'success') {
    const { access, refresh: newRefresh } = response.data.data;
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefresh);
  }
  
  return response.data;
};

/**
 * Get user profile
 */
const getProfile = async () => {
  const response = await axios.get(`${API_URL}/profile/`);
  
  if (response.data.status === 'success') {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.data));
  }
  
  return response.data;
};

/**
 * Update user profile
 */
const updateProfile = async (data) => {
  const response = await axios.patch(`${API_URL}/profile/`, data);
  
  if (response.data.status === 'success') {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.data));
  }
  
  return response.data;
};

/**
 * Change password
 */
const changePassword = async (data) => {
  const response = await axios.post(`${API_URL}/change-password/`, data);
  return response.data;
};

/**
 * Get activity logs
 */
const getActivityLogs = async (params = {}) => {
  const response = await axios.get('/accounts/activities/', { params });
  return response.data;
};

const authService = {
  login,
  logout,
  refreshToken,
  getProfile,
  updateProfile,
  changePassword,
  getActivityLogs,
};

export default authService;